import React, { useState } from 'react';
import { MonthlyPayrollData, DataSourceType } from '../types/PayrollTypes';
import { PayrollEstimation } from '../utils/payrollEstimation';
import './BonusForm.css';

interface BonusFormProps {
  currentYear: number;
  existingBonusMonths: number[];
  monthlyData: MonthlyPayrollData[];
  onAddBonus: (bonusData: MonthlyPayrollData) => void;
}

const BonusForm: React.FC<BonusFormProps> = ({ currentYear, existingBonusMonths, monthlyData, onAddBonus }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(6);
  const [bonusMonths, setBonusMonths] = useState<string>('1');
  const [isDetailMode, setIsDetailMode] = useState(false);
  const [grossSalary, setGrossSalary] = useState<string>('');
  const [healthInsurance, setHealthInsurance] = useState<string>('');
  const [employmentInsurance, setEmploymentInsurance] = useState<string>('');
  const [nationalPension, setNationalPension] = useState<string>('');
  const [incomeTax, setIncomeTax] = useState<string>('');
  const [localTax, setLocalTax] = useState<string>('');
  const [definedContribution, setDefinedContribution] = useState<string>('');

  const availableMonths = Array.from({ length: 12 }, (_, i) => i + 1)
    .filter(month => !existingBonusMonths.includes(month));

  // 平均月給を計算
  const getAverageMonthlySalary = () => {
    const salaryData = monthlyData.filter(data => 
      !data.isBonus && 
      data.grossSalary > 0 &&
      (data.sourceType === DataSourceType.PDF_IMPORTED || data.sourceType === DataSourceType.USER_EDITED)
    );
    
    if (salaryData.length === 0) return 0;
    
    const totalSalary = salaryData.reduce((sum, data) => sum + data.grossSalary, 0);
    return Math.round(totalSalary / salaryData.length);
  };

  const averageMonthlySalary = getAverageMonthlySalary();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    let bonusData: MonthlyPayrollData;
    
    if (isDetailMode) {
      // 詳細入力モード
      bonusData = {
        year: currentYear,
        month: selectedMonth,
        isBonus: true,
        grossSalary: parseFloat(grossSalary) || 0,
        healthInsurance: parseFloat(healthInsurance) || 0,
        employmentInsurance: parseFloat(employmentInsurance) || 0,
        nationalPension: parseFloat(nationalPension) || 0,
        incomeTax: parseFloat(incomeTax) || 0,
        localTax: parseFloat(localTax) || 0,
        definedContribution: parseFloat(definedContribution) || 0,
        sourceType: DataSourceType.USER_EDITED,
        lastUpdated: new Date().toISOString()
      };
    } else {
      // 平均月給×ヶ月モード
      const multiplier = parseFloat(bonusMonths) || 1;
      const estimatedBonus = PayrollEstimation.estimateBonus(monthlyData, currentYear);
      const calculatedGrossSalary = Math.round(averageMonthlySalary * multiplier);
      
      bonusData = {
        year: currentYear,
        month: selectedMonth,
        isBonus: true,
        grossSalary: calculatedGrossSalary,
        healthInsurance: Math.round((estimatedBonus.healthInsurance || 0) * multiplier),
        employmentInsurance: Math.round((estimatedBonus.employmentInsurance || 0) * multiplier),
        nationalPension: Math.round((estimatedBonus.nationalPension || 0) * multiplier),
        incomeTax: 0,
        localTax: 0,
        definedContribution: Math.round((estimatedBonus.definedContribution || 0) * multiplier),
        sourceType: DataSourceType.USER_EDITED,
        lastUpdated: new Date().toISOString()
      };
    }

    onAddBonus(bonusData);
    
    // フォームをリセット
    resetForm();
  };

  const resetForm = () => {
    setGrossSalary('');
    setHealthInsurance('');
    setEmploymentInsurance('');
    setNationalPension('');
    setIncomeTax('');
    setLocalTax('');
    setDefinedContribution('');
    setBonusMonths('1');
    setIsDetailMode(false);
    setIsOpen(false);
  };

  const handleCancel = () => {
    resetForm();
  };

  if (availableMonths.length === 0) {
    return null; // 追加可能な月がない場合は表示しない
  }

  return (
    <div className="bonus-form">
      {!isOpen ? (
        <button 
          className="add-bonus-btn primary-btn"
          onClick={() => setIsOpen(true)}
        >
          + 賞与月を追加
        </button>
      ) : (
        <div className="bonus-form-container">
          <h4>賞与データ追加</h4>
          
          <form onSubmit={handleSubmit} className="compact-form">
            {/* 基本情報行 */}
            <div className="form-inline">
              <label className="inline-field">
                支給月:
                <select 
                  value={selectedMonth} 
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                >
                  {availableMonths.map(month => (
                    <option key={month} value={month}>
                      {month}月
                    </option>
                  ))}
                </select>
              </label>

              {!isDetailMode ? (
                <>
                  <div className="inline-field">
                    <span style={{ fontSize: '0.85rem', color: '#555' }}>
                      平均月給 {averageMonthlySalary.toLocaleString()}円 × 
                    </span>
                  </div>
                  <label className="inline-field">
                    ヶ月:
                    <input
                      type="number"
                      value={bonusMonths}
                      onChange={(e) => setBonusMonths(e.target.value)}
                      step="0.1"
                      min="0"
                      style={{ width: '60px' }}
                    />
                  </label>
                  <div className="inline-field">
                    <span style={{ fontSize: '0.85rem', color: '#007bff', fontWeight: 'bold' }}>
                      = {(averageMonthlySalary * (parseFloat(bonusMonths) || 1)).toLocaleString()}円
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <label className="inline-field">
                    総支給額:
                    <input
                      type="number"
                      value={grossSalary}
                      onChange={(e) => setGrossSalary(e.target.value)}
                      placeholder="0"
                    />
                  </label>

                  <label className="inline-field">
                    健保:
                    <input
                      type="number"
                      value={healthInsurance}
                      onChange={(e) => setHealthInsurance(e.target.value)}
                      placeholder="0"
                    />
                  </label>

                  <label className="inline-field">
                    雇保:
                    <input
                      type="number"
                      value={employmentInsurance}
                      onChange={(e) => setEmploymentInsurance(e.target.value)}
                      placeholder="0"
                    />
                  </label>

                  <label className="inline-field">
                    厚年:
                    <input
                      type="number"
                      value={nationalPension}
                      onChange={(e) => setNationalPension(e.target.value)}
                      placeholder="0"
                    />
                  </label>

                  <label className="inline-field">
                    所得税:
                    <input
                      type="number"
                      value={incomeTax}
                      onChange={(e) => setIncomeTax(e.target.value)}
                      placeholder="0"
                    />
                  </label>

                  <label className="inline-field">
                    住民税:
                    <input
                      type="number"
                      value={localTax}
                      onChange={(e) => setLocalTax(e.target.value)}
                      placeholder="0"
                    />
                  </label>

                  <label className="inline-field">
                    DC:
                    <input
                      type="number"
                      value={definedContribution}
                      onChange={(e) => setDefinedContribution(e.target.value)}
                      placeholder="0"
                    />
                  </label>
                </>
              )}

              <div className="inline-actions">
                {!isDetailMode && (
                  <button 
                    type="button" 
                    className="detail-btn secondary-btn"
                    onClick={() => setIsDetailMode(true)}
                  >
                    詳細入力
                  </button>
                )}
                {isDetailMode && (
                  <button 
                    type="button" 
                    className="simple-btn secondary-btn"
                    onClick={() => setIsDetailMode(false)}
                  >
                    簡単入力
                  </button>
                )}
                <button type="submit" className="submit-btn primary-btn">
                  追加
                </button>
                <button type="button" className="cancel-btn secondary-btn" onClick={handleCancel}>
                  キャンセル
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default BonusForm;